// Useful Articles (blog) for Step Zone — educational, no dates shown.
// Content is general information, not financial advice.

export const ARTICLES = [
  {
    slug: "lower-monthly-bills",
    title: "7 Simple Ways to Lower Your Monthly Bills",
    category: "Lower expenses",
    excerpt: "Small tweaks to recurring costs can add up to real savings every month — here's where to start.",
    sections: [
      { heading: "Audit your subscriptions", body: ["List every recurring charge, then cancel anything you haven't used in the last 30 days. Free trials that quietly renewed are the usual culprits."] },
      { heading: "Call and negotiate", body: ["Internet, phone, and insurance providers often have retention offers. A five-minute call asking for a better rate — or mentioning a competitor — frequently works."] },
      { heading: "Bundle and switch", body: ["Compare bundled plans and shop your insurance annually. Loyalty rarely pays; the best rates usually go to new customers."] },
    ],
    takeaway: "Review recurring charges quarterly. Cutting even $50/month is $600 a year back in your pocket.",
  },
  {
    slug: "emergency-fund-from-scratch",
    title: "How to Build an Emergency Fund from Scratch",
    category: "Savings",
    excerpt: "A starter safety net doesn't require a big income — just a system that runs on autopilot.",
    sections: [
      { heading: "Start with a small target", body: ["Aim for $1,000 first. It's achievable and covers most minor emergencies without reaching for a credit card."] },
      { heading: "Automate it", body: ["Set up an automatic transfer the day after payday, even if it's $25. You can't spend what you don't see."] },
      { heading: "Grow to 3–6 months", body: ["Once you hit your starter goal, build toward three to six months of essential expenses in a separate high-yield savings account."] },
    ],
    takeaway: "Automate a small weekly transfer to a separate account. Consistency beats amount.",
  },
  {
    slug: "understand-your-credit-score",
    title: "Understanding Your Credit Score (and How to Improve It)",
    category: "Credit",
    excerpt: "Your score is built from a few clear factors. Knowing them makes it far easier to move the needle.",
    sections: [
      { heading: "What makes up your score", body: ["Payment history and amounts owed drive most of it, followed by length of history, new credit, and credit mix."] },
      { heading: "Quick wins", body: ["Pay on time, every time, and keep balances below 30% of your limits. Both have an outsized effect."] },
      { heading: "Be patient", body: ["Negative marks fade over time and positive habits compound. Check your report yearly for errors and dispute anything inaccurate."] },
    ],
    takeaway: "On-time payments and low balances are the two biggest levers. Automate payments to protect both.",
  },
  {
    slug: "personal-loans-101",
    title: "Personal Loans 101: What to Know Before You Borrow",
    category: "Financing",
    excerpt: "Personal loans can be a smart tool or an expensive trap — the terms make all the difference.",
    sections: [
      { heading: "Understand APR, not just the rate", body: ["APR includes fees, so it's the truest cost comparison between offers. Always compare APR to APR."] },
      { heading: "Match the term to the need", body: ["Longer terms mean lower payments but more interest overall. Borrow the least you need for the shortest term you can afford."] },
      { heading: "Watch for fees", body: ["Origination fees and prepayment penalties can quietly raise your cost. Read the fine print before signing."] },
    ],
    takeaway: "Compare APR across lenders and borrow the minimum for the shortest comfortable term.",
  },
  {
    slug: "right-insurance-coverage",
    title: "Do You Have the Right Insurance Coverage?",
    category: "Insurance",
    excerpt: "Being under- or over-insured both cost you. A quick review keeps your coverage matched to your life.",
    sections: [
      { heading: "Review after life changes", body: ["A move, marriage, new baby, or new car are all triggers to re-check your coverage limits and beneficiaries."] },
      { heading: "Mind the deductible trade-off", body: ["A higher deductible lowers your premium but means more out-of-pocket in a claim. Pick a level your emergency fund can cover."] },
      { heading: "Shop it around", body: ["Rates drift over time. Getting quotes every year or two often uncovers meaningful savings for the same coverage."] },
    ],
    takeaway: "Re-check coverage after major life events and re-quote every year or two.",
  },
  {
    slug: "budgeting-that-sticks",
    title: "Budgeting Methods That Actually Stick",
    category: "Savings",
    excerpt: "The best budget is the one you'll keep using. Here are a few approaches worth trying.",
    sections: [
      { heading: "The 50/30/20 rule", body: ["Roughly 50% of take-home to needs, 30% to wants, and 20% to savings and debt. Simple and flexible."] },
      { heading: "Zero-based budgeting", body: ["Give every dollar a job until income minus outgo equals zero. Great for people who want tight control."] },
      { heading: "Pick one and iterate", body: ["Don't chase the perfect system. Start with whichever feels easiest and adjust as you learn your patterns."] },
    ],
    takeaway: "Start simple with 50/30/20, then refine. A used budget beats a perfect one.",
  },
  {
    slug: "pay-off-debt-faster",
    title: "How to Pay Off Debt Faster",
    category: "Financing",
    excerpt: "Two proven strategies help you clear balances quicker — and stay motivated along the way.",
    sections: [
      { heading: "Avalanche vs. snowball", body: ["Avalanche targets the highest interest rate first (saves the most money). Snowball clears the smallest balance first (builds momentum)."] },
      { heading: "Free up extra to throw at it", body: ["Every dollar you cut from bills can go straight to your target balance. Small amounts accelerate payoff surprisingly fast."] },
      { heading: "Consider consolidation carefully", body: ["A lower-rate consolidation loan can help — but only if you avoid running the old balances back up."] },
    ],
    takeaway: "Pick avalanche to save money or snowball to stay motivated, then attack one balance at a time.",
  },
  {
    slug: "should-you-refinance",
    title: "Should You Refinance? A Simple Checklist",
    category: "Financing",
    excerpt: "Refinancing can lower payments or interest — if the numbers and timing line up.",
    sections: [
      { heading: "Check the rate gap", body: ["Refinancing usually makes sense when you can lower your rate meaningfully, not just slightly."] },
      { heading: "Factor in closing costs", body: ["Calculate your break-even point: how long until the savings outweigh the fees. If you'll move before then, it may not be worth it."] },
      { heading: "Mind the term reset", body: ["Extending the term lowers payments but can increase total interest. Keep the payoff date in view."] },
    ],
    takeaway: "Refinance when the rate savings clear your closing costs before you'd sell or move.",
  },
  {
    slug: "save-on-everyday-spending",
    title: "Smart Ways to Save on Everyday Spending",
    category: "Lower expenses",
    excerpt: "You don't need to deprive yourself — a few habits quietly trim the cost of daily life.",
    sections: [
      { heading: "Plan meals and groceries", body: ["A simple weekly plan and a list cut impulse buys and food waste, two of the biggest household leaks."] },
      { heading: "Use the 24-hour rule", body: ["For non-essentials, wait a day before buying. Most of the urge — and the purchase — disappears."] },
      { heading: "Stack the savings", body: ["Cash-back tools, store loyalty, and buying staples in bulk each help a little; together they add up."] },
    ],
    takeaway: "Meal-plan, pause before impulse buys, and stack small savings on the things you already buy.",
  },
  {
    slug: "building-credit-starting-out",
    title: "Building Credit When You're Starting Out",
    category: "Credit",
    excerpt: "No credit history? These steps help you build a solid foundation from zero.",
    sections: [
      { heading: "Start with the right product", body: ["A secured card or credit-builder loan is designed for people with no history and reports to the bureaus."] },
      { heading: "Keep usage low and pay in full", body: ["Use a small amount each month and pay the statement in full. This builds history without interest or risk."] },
      { heading: "Add positive history", body: ["Becoming an authorized user on a responsible person's account, or reporting rent/utilities, can help your file grow."] },
    ],
    takeaway: "Start with a secured card, use it lightly, and always pay in full to build history safely.",
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
